import { useMemo } from "react";
import {
    useEditor as useTipTap,
    Editor,
    EditorOptions,
    Extension,
} from "@tiptap/react";
import Document from "@tiptap/extension-document";
import Text from "@tiptap/extension-text";
import Paragraph from "@tiptap/extension-paragraph";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import HardBreak from "@tiptap/extension-hard-break";
import History from "@tiptap/extension-history";
import Underline from "@tiptap/extension-underline";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import Gapcursor from "@tiptap/extension-gapcursor";
import TextAlign from "@tiptap/extension-text-align";

// Note: All custom modules are now passed as props instead of being imported directly
// This allows the new project to be independent of the old project's module structure

// Type definitions for MobX stores
interface DictionaryStore {
    [key: string]: any;
}

interface AbbreviationStore {
    [key: string]: any;
}

interface CitationsStore {
    [key: string]: any;
}

interface ImageStore {
    [key: string]: any;
}

interface TableStore {
    [key: string]: any;
}

interface CommentStore {
    [key: string]: any;
}

interface CrossReferenceStore {
    [key: string]: any;
}

interface CaptisLinkStore {
    [key: string]: any;
}

// Main configuration interface for MobX stores
interface EditorStores {
    dictionaryStore: DictionaryStore;
    abbreviationStore: AbbreviationStore;
    citationsStore: CitationsStore;
    imageStore: ImageStore;
    tableStore: TableStore;
    commentStore: CommentStore;
    crossReferenceStore: CrossReferenceStore;
    captisLinkStore: CaptisLinkStore;
}

// Content type definition
interface DocumentContent {
    type: string;
    content?: any[];
    [key: string]: any;
}

// Metadata interface
interface EditorMetadata {
    [key: string]: any;
}

// Custom modules interface - all the modules that need to be passed from the old project
interface CustomModules {
    // Core modules
    CaptionListItem: Extension;
    DictionaryPlugin: Extension;
    LoadingPlugin: Extension;
    ZoomPlugin: Extension;
    ClipboardPlugin: Extension;
    LandscapePlugin: Extension;
    CustomCommands: Extension;
    PastePlugin: Extension;
    OrderedList: Extension;
    Table: Extension;
    TableDependencies: Extension[];
    PreserveMergeOnPaste: Extension;

    // Version and marks
    InsertMark: Extension;
    DeleteMark: Extension;
    VersionExtension: Extension;

    // Content modules
    Image: Extension;
    Abbreviation: Extension;
    CommentMark: Extension;
    PageBreak: Extension;
    Citation: Extension;
    CitationGroup: Extension;
    CrossReference: Extension;
    CaptisLinkMark: Extension;
    Link: Extension;
    NodePosition: Extension;
    HelpTextMark: Extension;
    SampleTextMark: Extension;
    SymbolPicker: Extension;
    CustomObject: Extension;
    HighlightMark: Extension;
    ReportView: Extension;

    // Paragraph and list modules
    Paragraph: Extension;
    ListItem: Extension;
    BulletList: Extension;
    verticalAlign: Extension;

    // Utility functions and constants
    processCitations: (
        content: DocumentContent | undefined,
    ) => DocumentContent | undefined;
    validElements: string[];
    ReportDocumentSection: {
        HELP_TEXT: any;
        EXTERNAL_LINK: {
            validate: (url: string) => boolean;
        };
    };
}

// Main hook configuration interface
interface UseEditorConfig {
    content?: DocumentContent;
    readonly?: boolean;
    onUpdate?: (props: { editor: Editor }) => void;
    metadata?: EditorMetadata;
    editorProps?: EditorOptions["editorProps"];
    templateMode?: boolean;
    stores: EditorStores;
    modules: CustomModules;
    customObject?: any;
}

/**
 * Custom hook for TipTap editor with TypeScript support
 * Integrates all custom plugins and MobX stores passed as props
 */
export function useEditor({
    content,
    readonly = false,
    onUpdate,
    metadata,
    editorProps,
    templateMode = false,
    stores,
    modules,
}: UseEditorConfig): Editor | null {
    console.log("Configuring Editor V2");

    const safeConfigure = (plugin: any, dependencies: any) => {
        return plugin ? plugin.configure(dependencies) : null;
    };

    // Destructure stores from props
    const {
        dictionaryStore,
        abbreviationStore,
        citationsStore,
        imageStore,
        tableStore,
        commentStore,
        crossReferenceStore,
        captisLinkStore,
    } = stores || {};

    // Destructure custom modules from props
    const {
        CaptionListItem,
        DictionaryPlugin = null,
        LoadingPlugin,
        ZoomPlugin,
        ClipboardPlugin,
        LandscapePlugin,
        CustomCommands,
        PastePlugin,
        OrderedList,
        Table = null,
        TableDependencies,
        PreserveMergeOnPaste,
        InsertMark,
        DeleteMark,
        VersionExtension,
        Image = null,
        Abbreviation = null,
        CommentMark = null,
        PageBreak,
        Citation,
        CitationGroup,
        CrossReference = null,
        CaptisLinkMark,
        Link = null,
        NodePosition,
        HelpTextMark,
        SampleTextMark,
        SymbolPicker,
        CustomObject,
        HighlightMark,
        ReportView,
        ListItem,
        BulletList,
        verticalAlign = null,
        processCitations,
        validElements,
        ReportDocumentSection,
    } = modules || {};

    const baseExtensions = [
        Document,
        modules?.Paragraph ?? Paragraph, // default to the base extension
        Text,
        HardBreak,
        Bold,
        Italic,
        Underline,
        Subscript,
        Superscript,
        LoadingPlugin,
        LandscapePlugin,
        PastePlugin,
        safeConfigure(Table, {
            store: tableStore,
            resizable: true,
            metadata,
        }),
        PreserveMergeOnPaste,
        OrderedList,
        ListItem,
        BulletList,
        PageBreak,
        CaptionListItem,
        safeConfigure(Image, {
            store: imageStore,
            metadata,
        }),

        TextAlign.configure({
            types: ["paragraph"],
            defaultAlignment: "justify",
        }),

        safeConfigure(verticalAlign, {
            types: validElements,
        }),

        // Conditional extensions based on template mode
        ...(!templateMode
            ? [
                  safeConfigure(CaptisLinkMark, {
                      store: captisLinkStore,
                  }),
                  safeConfigure(Citation, {
                      store: citationsStore,
                  }),
                  CitationGroup,
              ]
            : []),

        safeConfigure(CrossReference, {
            store: crossReferenceStore,
        }),
        safeConfigure(DictionaryPlugin, {
            dictionary: dictionaryStore,
            readonly,
        }),
        safeConfigure(Abbreviation, {
            abbreviation: abbreviationStore,
            readonly,
        }),

        safeConfigure(CommentMark, {
            comment: commentStore,
            readonly,
        }),

        safeConfigure(ReportView, {
            readonly,
        }),

        safeConfigure(HighlightMark, {
            multicolor: true,
        }),

        safeConfigure(Link, {
            autolink: true,
            linkOnPaste: true,
            validate: ReportDocumentSection?.EXTERNAL_LINK?.validate
                ? ReportDocumentSection?.EXTERNAL_LINK?.validate
                : () => {},
            HTMLAttributes: {
                // Setting to _blank caused to open the link twice in preview mode
                // This will still open in new tab but only once
                target: "#",
            },
        }),
        InsertMark,
        DeleteMark,
        CustomCommands,
        NodePosition,
        HelpTextMark,
        SampleTextMark,
        SymbolPicker,
        VersionExtension,
        CustomObject,
        ClipboardPlugin,
        ...(TableDependencies || []),
    ];

    // Add readonly-specific extensions
    if (!readonly) {
        baseExtensions.push(History, Gapcursor, ZoomPlugin);
    }

    baseExtensions;

    /**
     * Process and validate content
     */
    const processedContent = useMemo(() => {
        // let validatedContent = content;

        // Validate the content value
        if (content?.type !== "doc") {
            content = undefined;
        }

        if (readonly) {
            content = processCitations(content);
        }

        return content;
    }, [content]);

    /**
     * Initialize the editor
     */
    const editor = useTipTap(
        {
            editable: !readonly,
            extensions: baseExtensions,
            onUpdate,
            editorProps,
            content: processedContent,
        },
        [readonly], // Dependencies for the hook
    );

    return editor;
}

// Export types for use in other components
export type {
    UseEditorConfig,
    EditorStores,
    DocumentContent,
    EditorMetadata,
    CustomModules,
    DictionaryStore,
    AbbreviationStore,
    CitationsStore,
    ImageStore,
    TableStore,
    CommentStore,
    CrossReferenceStore,
    CaptisLinkStore,
};
